import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { KeyRound, Mail, ShieldCheck } from "lucide-react";
import {
  extractTeacherRecord,
  extractTeacherToken,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  clearTeacherSignupContext,
  setTeacherSession,
} from "../../features/teacher/teacherSlice";
import {
  TeacherAuthField,
  TeacherAuthLayout,
  TeacherFeedbackMessage,
} from "./TeacherUI";
import { getTeacherAuthErrorMessage } from "../../utils/uiErrorMessages";

export default function TeacherOtpVerification() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const signupContext = useSelector((state) => state.teacher?.signupContext);
  const [formData, setFormData] = useState({
    pre_auth_token:
      location.state?.preAuthToken || signupContext?.preAuthToken || "",
    otp: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalMessage, setGeneralMessage] = useState("");

  const email = useMemo(
    () => String(location.state?.email || signupContext?.email || "").trim(),
    [location.state, signupContext]
  );

  const otpMutation = useMutation({
    mutationFn: teacherApi.verifyOtp,
    onSuccess: (payload) => {
      const teacherToken = extractTeacherToken(payload);
      const teacherRecord = extractTeacherRecord(payload, {
        email,
      });

      if (!teacherToken) {
        setGeneralMessage(
          "OTP verification succeeded but the backend did not return a teacher JWT."
        );
        return;
      }

      dispatch(
        setTeacherSession({
          token: teacherToken,
          teacher: teacherRecord,
        })
      );
      dispatch(clearTeacherSignupContext());
      navigate("/teacherdashboard", { replace: true });
    },
    onError: (error) => {
      setFieldErrors(error.fieldErrors || {});
      setGeneralMessage(getTeacherAuthErrorMessage(error, "otp"));
    },
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (fieldErrors[name]) {
      setFieldErrors((previous) => ({
        ...previous,
        [name]: "",
      }));
    }

    if (generalMessage) {
      setGeneralMessage("");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFieldErrors({});
    setGeneralMessage("");

    const nextErrors = {};

    if (!formData.pre_auth_token.trim()) {
      nextErrors.pre_auth_token = "pre_auth_token is required.";
    }

    if (!formData.otp.trim()) {
      nextErrors.otp = "OTP is required.";
    }

    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    otpMutation.mutate({
      pre_auth_token: formData.pre_auth_token.trim(),
      otp: formData.otp.trim(),
    });
  };

  return (
    <TeacherAuthLayout
      title="Verify OTP"
      subtitle="Enter the OTP sent by the backend to finish teacher signup."
      closeTo="/teachersignup"
      footer={
        <p className="teacher-auth-footer-text">
          Need to start over?{" "}
          <Link className="teacher-auth-link" to="/teachersignup">
            Back to signup
          </Link>
        </p>
      }
    >
      {email ? (
        <TeacherFeedbackMessage variant="info">
          OTP verification is ready for <strong>{email}</strong>.
        </TeacherFeedbackMessage>
      ) : null}
      {generalMessage ? (
        <TeacherFeedbackMessage variant="error">{generalMessage}</TeacherFeedbackMessage>
      ) : null}

      <form className="teacher-auth-form" onSubmit={handleSubmit} noValidate>
        <input
          type="hidden"
          name="pre_auth_token"
          value={formData.pre_auth_token}
        />

        <TeacherAuthField
          id="teacher-otp"
          label="OTP"
          icon={ShieldCheck}
          name="otp"
          type="text"
          value={formData.otp}
          onChange={handleChange}
          placeholder="Enter the OTP"
          error={fieldErrors.otp}
        />

        <TeacherAuthField
          id="teacher-otp-email"
          label="Signup Email"
          icon={Mail}
          name="signup_email"
          type="text"
          value={email}
          readOnly
          note="Displayed for confirmation only."
        />

        <button className="teacher-auth-button" type="submit" disabled={otpMutation.isPending}>
          {otpMutation.isPending ? "Verifying OTP..." : "Verify and Continue"}
        </button>
      </form>
    </TeacherAuthLayout>
  );
}
